import { getThumbIcon } from 'components/DomainIcon/apis'
import orgChartHelper from 'pages/contacts/components/OrgChart/Helper'
import contactHelper from 'pages/contacts/components/SharedContacts/Helper'
import * as contactService from 'pages/contacts/modules/contactService'

import { emailAddress } from 'utils'
import { getInitialAvatarSrc } from 'utils/ui'
import { getAppServices } from 'env'

interface ContactInfo {
  id: string
  avatarUrl: string | null
  name: string | null
  email: string
}

export async function getContactInfo(email: string): Promise<ContactInfo | null> {
  let contactInfo: ContactInfo | null = null
  const orgChartContact = await orgChartHelper.findContact(email)
  if (orgChartContact != null) {
    contactInfo = {
      id: email,
      avatarUrl: orgChartContact.profile_photo_url,
      name: orgChartContact.display_name,
      email,
    }
  } else {
    const contactData = await contactHelper.findContact(email)
    if (contactData != null) {
      const { person, photo } = contactData
      contactInfo = {
        id: email,
        avatarUrl: photo != null && photo.url != null ? photo.url : null,
        name: person != null && person.display_name != null ? person.display_name : null,
        email,
      }
    }
  }

  return contactInfo
}

interface AvatarStep {
  step: number
  promise: Promise<string | null>
}

function* getAvatarUrlByStep(
  email: string,
  skipLogo = false,
): Generator<AvatarStep, void, unknown> {
  if (email != null) {
    yield {
      step: 0,
      promise: orgChartHelper.getAvatarUrlByEmail(email),
    }

    const { pages } = getAppServices() || {}

    // chat desktop session 일떄 getAvatarUrlByStep를 사용하지만 contact은 사용하지 않기때문에 조건문 추가
    if (pages?.contacts !== false) {
      yield {
        step: 1,
        promise: contactHelper.getAvatarUrlByEmail(email),
      }

      yield {
        step: 2,
        promise: contactService.getPhotoWithEmail(email),
      }
    }

    const emailInfo = emailAddress(email)

    if (skipLogo !== true && emailInfo != null) {
      yield {
        step: 3,
        promise: getThumbIcon(emailInfo.host, 72),
      }
    }
  }
}

export interface AvatarGen {
  key: string
  name: string | null
  email: string
  loading: boolean
  prevUrls: string[]
  curUrl: string | null
  fixed: boolean
  curMain: ((url: string | null, fixed: boolean) => void) | null
  listeners: ((url: string | null, fixed: boolean) => void)[]
  hasNext: boolean
  defaultSrc: string
  fixUrl?: string
  avatarGen: Generator<AvatarStep, void, unknown>
  setFixed: (url: string, forced?: boolean) => void
  callListener: (listener: (url: string | null, fixed: boolean) => void) => void
  callListeners: () => void
  next: (listener: (url: string | null, fixed: boolean) => void) => void
  addListener: (listener: (url: string | null, fixed: boolean) => void) => void
  removeListener: (listener: (url: string | null, fixed: boolean) => void) => void
  spinning?: boolean
  spiningTimer?: NodeJS.Timeout | null
}

const AvatarMap: { [key: string]: AvatarGen } = {}

// 계정 전환/로그아웃(DataManager.init) 시 호출 — 이전 계정의 아바타 생성기·listener 참조를 해제한다.
// clear 후 getAvatarGen이 같은 key로 재생성하므로 기능 영향은 없고, 마운트된 컴포넌트가 보유한
// 옛 gen은 독립적으로 동작하다가 리마운트 시 새 gen으로 대체된다.
export function clearAvatarCache(): void {
  Object.keys(AvatarMap).forEach((key) => {
    delete AvatarMap[key]
  })
}

export function checkLog(email: string, text: string, ...params: unknown[]) {
  if (email === 'seonghun12@dreamwiz.com') {
    console.log(` --- imgCheck ${text} : `, ...params)
  }
}

export function getAvatarGen(name: string | null, email: string, skipLogo = false): AvatarGen {
  const key = `${email}=${name}=${skipLogo}`
  if (AvatarMap[key] == null) {
    const prevUrls: string[] = []

    const defaultSrc = getInitialAvatarSrc(name, email)

    const genObj: AvatarGen = {
      key,
      name,
      email,
      loading: false,
      prevUrls,
      curUrl: defaultSrc,
      fixed: false,
      curMain: null,
      listeners: [],
      hasNext: true,
      defaultSrc,
      avatarGen: getAvatarUrlByStep(email, skipLogo),
      setFixed: (url: string, forced = false) => {
        if (genObj.fixed !== true && (forced === true || genObj.defaultSrc !== url)) {
          genObj.fixed = true
          if (genObj.fixUrl !== url) {
            genObj.fixUrl = url
            genObj.callListeners()
          }
        }
      },
      callListener: (listener: (url: string | null, fixed: boolean) => void) => {
        if (genObj.fixed === true) {
          listener(genObj.fixUrl!, true)
        } else if (genObj.curUrl != null) {
          listener(genObj.curUrl, false)
        }
      },
      callListeners: () => {
        let url: string | null = null
        const { fixed } = genObj
        if (fixed === true) {
          url = genObj.fixUrl!
        } else if (genObj.curUrl != null) {
          url = genObj.curUrl
        }
        if (url != null) {
          const { listeners } = genObj
          for (let i = listeners.length - 1; i >= 0; i -= 1) {
            const listener = listeners[i]
            listener(url, fixed)
          }
        }
      },
      next: (listener: (url: string | null, fixed: boolean) => void) => {
        if (genObj.fixed !== true && genObj.hasNext === true && listener === genObj.curMain) {
          const nextObj = genObj.avatarGen.next()
          if (nextObj != null) {
            const { value, done } = nextObj
            if (done === true) {
              genObj.hasNext = false
              genObj.curUrl = defaultSrc

              genObj.setFixed(defaultSrc, true)
            } else if (value != null) {
              const { step, promise } = value
              promise.then((url) => {
                checkLog(genObj.email, ' - step - ', step, url)
                genObj.curUrl = url

                if (url == null || genObj.prevUrls.indexOf(url) >= 0) {
                  genObj.next(genObj.curMain!)
                } else {
                  genObj.prevUrls.push(url)
                  genObj.callListeners()
                }
              })
            }
          }
        }
      },
      addListener: (listener: (url: string | null, fixed: boolean) => void) => {
        if (genObj.listeners.indexOf(listener) < 0) {
          genObj.listeners.push(listener)
          if (genObj.listeners.length > 0) {
            genObj.curMain = genObj.listeners[0]
          }
          genObj.callListener(listener)
        }
      },
      removeListener: (listener: (url: string | null, fixed: boolean) => void) => {
        const pos = genObj.listeners.indexOf(listener)
        if (pos >= 0) {
          genObj.listeners.splice(pos, 1)
        }
        if (genObj.listeners.length > 0) {
          genObj.curMain = genObj.listeners[0]
        } else {
          genObj.curMain = null
        }
      },
    }

    AvatarMap[key] = genObj
  }
  return AvatarMap[key]
}

export function getAvatarURL(
  name: string | null,
  email: string,
  skipLogo?: boolean,
): Promise<string> {
  return new Promise((resolve) => {
    const gen = getAvatarGen(name, email, skipLogo)

    if (gen.fixed === true) {
      resolve(gen.fixUrl!)
    } else if (gen.loading !== true) {
      gen.loading = true
      const img = new Image()

      const urlCallback0 = (url: string | null, fixed: boolean) => {
        if (fixed !== true) {
          if (url != null) {
            img.src = url
          }
        } else {
          gen.removeListener(urlCallback0)
          resolve(url!)
        }
      }

      img.onload = () => {
        if (img.src !== gen.defaultSrc) {
          gen.setFixed(img.src)
          gen.removeListener(urlCallback0)
          resolve(img.src)
        }
      }

      img.onerror = () => {
        if (gen.hasNext === true) {
          gen.fixed = false
          gen.addListener(urlCallback0)
          gen.next(urlCallback0)
        } else {
          // 더 시도할 step이 없으면 defaultSrc로 종결 — 종결하지 않으면 listener와
          // pending Promise(호출자 클로저)가 영구 잔류한다.
          gen.setFixed(gen.defaultSrc, true)
          // setFixed는 fixUrl이 이미 같으면 통지를 생략하므로 직접 정리로 종결을 보장한다.
          gen.removeListener(urlCallback0)
          resolve(gen.fixUrl ?? gen.defaultSrc)
        }
      }

      gen.addListener(urlCallback0)

      gen.next(urlCallback0)
    } else {
      const urlCallback1 = (url: string | null, fixed: boolean) => {
        if (fixed === true) {
          gen.removeListener(urlCallback1)
          resolve(url!)
        }
      }

      gen.addListener(urlCallback1)
    }
  })
}
